import { EntityUIComponentProps, EntityUIComponentWithChildrenProps } from '.';
import { SettingsErrorLogger } from '../../utils/logger';


export interface EntityUIComponentInfo {
    readonly componentFunc: React.FC<EntityUIComponentProps & EntityUIComponentWithChildrenProps>;
    readonly customSettingsValidator?: ((
        obj: unknown,
        errorLogger?: SettingsErrorLogger | undefined,
        currentConfigPath?: readonly string[] | undefined
    )=> boolean) | undefined;
}


/**
 * This is an EntityUIComponentCollection which contains all the entity UI components exist in the app. Any component registered with
 * this collection can be used in the Entity Details page. Whenever we build a new entity UI component, we must call the 'registerComponent'
 * function to register the new component before we can start using it.
 */
export class EntityUIComponentCollection {
    private static instance = new EntityUIComponentCollection();

    private uiComponents = new Map<string, EntityUIComponentInfo>();

    public static getInstance () {
        return EntityUIComponentCollection.instance;
    }

    /**
     * Register a new component with the EntityUIComponentCollection.
     * @param compType - Must be a new component type name that DOES NOT already exist in the collection or else an Error will be thrown.
     * @param componentInfo - An object containing the component function and an OPTIONAL customSettings object validator function that can be used
     *                        for validating the customSettings object for the component.
     */
    public registerComponent (compType: string, componentInfo: EntityUIComponentInfo) {
        if (this.uiComponents.has(compType)) {
            throw new Error(`Component already existed '${compType}'`);
        }
        this.uiComponents.set(compType, componentInfo);
    }


    public hasComponentType (compType: string): boolean {
        return this.uiComponents.has(compType);
    }


    public getAllComponentTypes (): readonly string[] {
        return Array.from(this.uiComponents.keys());
    }


    public getComponent (compType: string): EntityUIComponentInfo | undefined {
        return this.uiComponents.get(compType);
    }
}
